// ============================================================================
// HEADER COMPONENT - Shared header for all pages
// ============================================================================

import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
  LayoutDashboard, FileText, Settings, LogOut, 
  Menu, X, Crown, ChevronRight 
} from 'lucide-react';
import { useAuth } from '../lib/AuthContext';
import LanguageSelector from './LanguageSelector';

/**
 * Header Component
 * @param {'dark' | 'light' | 'transparent'} variant - Header style
 * @param {boolean} showNav - Show navigation links
 * @param {boolean} showAuth - Show auth buttons (login/dashboard)
 * @param {boolean} showLanguage - Show language selector
 * @param {boolean} minimal - Minimal mode (just logo)
 */
const Header = ({ 
  variant = 'light',
  showNav = true,
  showAuth = true,
  showLanguage = true,
  minimal = false
}) => {
  const { t, i18n } = useTranslation();
  const { user, profile, signOut, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const isRTL = i18n.dir() === 'rtl';
  const isPro = profile?.subscription_status === 'active';

  // Style variants
  const variants = {
    dark: {
      bg: 'bg-[#0a0b14] border-b-4 border-teal-500',
      text: 'text-gray-300',
      textHover: 'hover:text-white',
      textActive: 'text-teal-400',
      logoVariant: 'dark'  // dark header = use light/white logo
    },
    light: {
      bg: 'bg-white/80 backdrop-blur-md border-b border-gray-100',
      text: 'text-gray-600',
      textHover: 'hover:text-gray-900',
      textActive: 'text-teal-600',
      logoVariant: 'light'  // light header = use dark logo
    },
    transparent: {
      bg: 'bg-transparent',
      text: 'text-gray-600',
      textHover: 'hover:text-gray-900',
      textActive: 'text-teal-600',
      logoVariant: 'light'  // transparent (usually light) = use dark logo
    }
  };

  const style = variants[variant] || variants.light;

  const handleSignOut = async () => {
    await signOut();
    navigate('/');
  };

  // Navigation items
  const navItems = [
    { path: '/dashboard', label: t('nav.dashboard'), icon: LayoutDashboard },
    { path: '/assessment', label: t('nav.assessment'), icon: FileText },
    { path: '/settings', label: t('nav.settings'), icon: Settings },
  ];

  const isActivePath = (path) => location.pathname === path;

  // Minimal header (just logo and language)
  if (minimal) {
    return (
      <header className={`${style.bg} sticky top-0 z-50`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="font-serif text-2xl font-bold">
              <span className={variant === 'dark' ? 'text-white' : 'text-gray-900'}>Lab</span>
              <span className="text-teal-500">2040</span>
            </Link>
            {showLanguage && <LanguageSelector compact />}
          </div>
        </div>
      </header>
    );
  }

  return (
    <header className={`${style.bg} sticky top-0 z-50`} dir={isRTL ? 'rtl' : 'ltr'}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo - Simple Text */}
          <Link to="/" className="font-serif text-2xl font-bold">
            <span className={variant === 'dark' ? 'text-white' : 'text-gray-900'}>Lab</span>
            <span className="text-teal-500">2040</span>
          </Link>

          {/* Desktop Navigation */}
          {showNav && isAuthenticated && (
            <nav className="hidden md:flex items-center gap-6">
              {navItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center gap-2 font-medium transition-colors ${
                    isActivePath(item.path) 
                      ? style.textActive 
                      : `${style.text} ${style.textHover}`
                  }`}
                >
                  <item.icon className="w-4 h-4" />
                  {item.label}
                </Link>
              ))}
            </nav>
          )}

          {/* Landing page nav (when not authenticated) */}
          {showNav && !isAuthenticated && (
            <nav className="hidden md:flex items-center gap-8">
              <a href="#features" className={`${style.text} ${style.textHover} font-medium`}>
                {t('nav.features')}
              </a>
              <a href="#faq" className={`${style.text} ${style.textHover} font-medium`}>
                {t('nav.faq')}
              </a>
            </nav>
          )}

          {/* Right side actions */}
          <div className="hidden md:flex items-center gap-4">
            {/* Language Selector */}
            {showLanguage && <LanguageSelector compact />}

            {showAuth && (
              <>
                {isAuthenticated ? (
                  <>
                    {/* Pro Badge */}
                    <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium ${
                      isPro
                        ? 'bg-gradient-to-r from-amber-100 to-orange-100 text-amber-700'
                        : variant === 'dark' 
                          ? 'bg-gray-800 text-gray-400'
                          : 'bg-gray-100 text-gray-600'
                    }`}>
                      {isPro && <Crown className="w-4 h-4" />}
                      {isPro ? t('subscription.proMember') : t('subscription.free')}
                    </div>

                    {/* User Avatar */}
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 bg-gradient-to-br from-teal-500 to-cyan-500 rounded-full flex items-center justify-center text-white font-medium">
                        {user?.email?.charAt(0).toUpperCase() || 'U'}
                      </div>
                      <button
                        onClick={handleSignOut}
                        className={`${style.text} ${style.textHover} transition-colors`}
                        title={t('nav.logout')}
                      >
                        <LogOut className="w-5 h-5" />
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <Link 
                      to="/login" 
                      className={`${style.text} ${style.textHover} font-medium`}
                    >
                      {t('auth.signIn')}
                    </Link>
                    <Link
                      to="/assessment"
                      className="px-4 py-2 bg-gradient-to-r from-teal-600 to-cyan-500 text-white font-semibold rounded-lg hover:from-teal-700 hover:to-cyan-600 transition-all"
                    >
                      {t('cta.getStarted')}
                    </Link>
                  </>
                )}
              </>
            )}
          </div>

          {/* Mobile menu button */}
          <button
            className="md:hidden p-2"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? (
              <X className={`w-6 h-6 ${style.text}`} />
            ) : (
              <Menu className={`w-6 h-6 ${style.text}`} />
            )}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileMenuOpen && (
        <div className={`md:hidden border-t ${variant === 'dark' ? 'border-gray-800 bg-[#0a0b14]' : 'border-gray-200 bg-white'}`}>
          <div className="px-4 py-3 space-y-3">
            {isAuthenticated ? (
              <>
                {navItems.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`flex items-center gap-2 py-2 font-medium ${
                      isActivePath(item.path) ? style.textActive : style.text
                    }`}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <item.icon className="w-4 h-4" />
                    {item.label}
                  </Link>
                ))}
                <div className="pt-3 border-t border-gray-200">
                  <LanguageSelector />
                </div>
                <button
                  onClick={handleSignOut}
                  className="flex items-center gap-2 text-red-600 py-2 w-full"
                >
                  <LogOut className="w-4 h-4" />
                  {t('nav.logout')}
                </button>
              </>
            ) : (
              <>
                <a href="#features" className={`block py-2 ${style.text}`}>
                  {t('nav.features')}
                </a>
                <a href="#faq" className={`block py-2 ${style.text}`}>
                  {t('nav.faq')}
                </a>
                <div className="pt-3 border-t border-gray-200">
                  <LanguageSelector />
                </div>
                <Link
                  to="/login"
                  className={`block py-2 ${style.text}`}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {t('auth.signIn')}
                </Link>
                <Link
                  to="/assessment"
                  className="block py-2 text-teal-600 font-semibold"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {t('cta.getStarted')}
                </Link>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
